<template>
  <div class="ai-tutor-turn-list" data-testid="ai-tutor-turn-list">
    <template v-for="turn in turns">
      <!-- Doc 27 (ST-03): the reply that is live in this page lifetime is
           shown through its presentation record -- the turn's own content is
           never changed, only how much of it is visible yet. Every other
           turn, history included, renders whole. -->
      <turn-bubble
        :key="turn.id"
        :role="turn.role === 'student' ? 'student' : 'tutor'"
        :content="contentFor(turn)"
        :meta="metaFor(turn)"
        :meta-title="metaTitleFor(turn)"
        :initial-source="studentInitialSource"
        :retry-lead="reteachTurnIds.includes(turn.id)"
        :media="mediaFor(turn)"
        :sources="isPresented(turn) ? [] : sourcesFor(turn)"
        :figures="turn.figures || []"
        :streaming="isPresented(turn)"
        :live-stage="isPresented(turn) ? 'writing' : ''"
        @media-load="follow(false)"
        @media-error="$emit('media-expired', turn.id)"
        @open-citation="openCitation(turn, $event)"
      />
      <!-- CHAT-UX-01 / CX-02 (doc 29 §5.2): the text read from a photo, kept
           under the tutor's question once that question has been answered.
           Never together with the pending card below. -->
      <chat-reading-record
        v-if="readings[turn.id]"
        :key="`${turn.id}-reading`"
        :draft="readings[turn.id].draft"
        :status="readings[turn.id].status"
      />
      <!-- FINAL CHAT V2: the tutor's pending question, answered in place
           (upstream `resume`). -->
      <chat-ask
        v-if="askTurn && askTurn.id === turn.id"
        :key="`${turn.id}-ask`"
        :ask="pendingAction"
        :disabled="actionsDisabled"
        :selected-option-id="selectedOptionId"
        @answer="$emit('answer-ask', $event)"
      />
      <!-- FINAL CHAT V2: the tutor's suggested next turns (V2
           `.related-paths`), right under the answer they belong to. -->
      <chat-suggestions
        v-if="suggestionsTurn && suggestionsTurn.id === turn.id"
        :key="`${turn.id}-suggestions`"
        :suggestions="turn.followUpSuggestions"
        :selected-id="selectedSuggestionId"
        :disabled="actionsDisabled"
        @pick="$emit('pick-suggestion', $event)"
      />
      <!-- STUDENT CHAT V2: «فهمیدم» recorded -- V2's `.learned-message`
           reply row, right under the turn it rates. -->
      <div
        v-if="isLearned(turn)"
        :key="`${turn.id}-learned`"
        data-testid="ai-tutor-feedback-learned"
      >
        <turn-bubble role="tutor">
          <div class="ai-tutor-turn-list__learned" role="status">
            <span><student-home-icon name="check" :size="19"/></span>
            <div>
              <strong>{{
                $t('aiTutorSetup.chat.feedback.learnedTitle')
              }}</strong>
              <p>{{ learnedBody(turn) }}</p>
            </div>
          </div>
        </turn-bubble>
      </div>
      <!--
        V405-F1 (doc 26 §12.3 / §12.4): the V405 end-of-chat panel
        (`.chat-learned-actions`), under the learned message of the LAST turn
        only, and only while nothing is in flight. Every action reports to the
        view; the practice action exists only when the tutor's own answer
        carries a self-test suggestion, and sends exactly that suggestion.
      -->
      <div
        v-if="completionTurn && completionTurn.id === turn.id"
        :key="`${turn.id}-completion`"
        class="ai-tutor-completion"
        :class="{ 'ai-tutor-completion--single': !practiceSuggestion }"
        role="group"
        :aria-label="$t('aiTutorSetup.chat.completion.label')"
        data-testid="ai-tutor-completion"
      >
        <button
          v-if="practiceSuggestion"
          type="button"
          class="ai-tutor-completion__secondary"
          data-testid="ai-tutor-completion-practice"
          :disabled="actionsDisabled"
          @click="$emit('pick-suggestion', practiceSuggestion)"
        >
          {{ $t('aiTutorSetup.chat.completion.practice') }}
        </button>
        <button
          type="button"
          class="ai-tutor-completion__primary"
          data-testid="ai-tutor-completion-end"
          @click="$emit('end-conversation')"
        >
          {{ $t('aiTutorSetup.chat.completion.end') }}
        </button>
        <button
          type="button"
          class="ai-tutor-completion__new"
          data-testid="ai-tutor-completion-new"
          :aria-label="$t('aiTutorSetup.chat.completion.newQuestionAria')"
          @click="$emit('new-conversation')"
        >
          <student-home-icon name="plus" :size="16" />
          <span>{{ $t('aiTutorSetup.chat.completion.newQuestion') }}</span>
        </button>
      </div>
    </template>

    <!-- STUDENT CHAT V2: the student's message, on screen from the moment of
         submit until the refetch returns the persisted turn. -->
    <div
      v-if="pendingTurn"
      :key="pendingTurn.localId"
      data-testid="ai-tutor-turn-pending"
    >
      <turn-bubble
        role="student"
        :content="pendingTurn.text"
        :meta="pendingMeta"
        :media="pendingMedia"
        :initial-source="studentInitialSource"
        @media-load="follow(false)"
        :delivery-state="pendingTurn.status === 'failed' ? 'failed' : ''"
        :resend-disabled="resendDisabled"
        @resend="$emit('resend')"
      />
    </div>

    <!--
      WP-03-CHAT-CORRECTION-R3 (doc 11): ONE live tutor bubble for the whole
      reply -- processing (nothing streamed yet), streaming (the text grows in
      this same bubble) and reconciling (streamed text held until the
      authoritative refetch has been applied) are all the same element, so it is
      never re-created per chunk and never flickers away on `done`. It is driven
      by processingState (adapter.js applySessionEvent), never a confirmed
      TutorTurn. Wrapped in its own testid'd element rather than passing
      data-testid as a fallthrough attribute -- TurnBubble's root already binds
      its own `data-testid` from `role`, which would otherwise win.

      STUDENT CHAT V2: the same element is also the waiting state from the
      moment of submit (`awaitingReply`), before the first SSE event -- so
      there is never dead time and never a second spinner.
    -->
    <div
      v-if="showLive"
      key="ai-tutor-live-reply"
      data-testid="ai-tutor-turn-provisional"
    >
      <turn-bubble
        role="tutor"
        :content="liveVisible"
        :loading="liveThinking"
        :streaming="isStreaming"
        :live-stage="liveStage"
        :thinking-phase="thinkingPhase"
        :thinking-since="thinkingSince"
        :thinking-leaving="handingOff"
      />
    </div>

    <chat-feedback
      v-if="promptTurn"
      key="ai-tutor-feedback-prompt"
      variant="prompt"
      :retry="reteachTurnIds.includes(promptTurn.id)"
      :busy="feedbackBusy"
      @feedback="kind => $emit('feedback', { turnId: promptTurn.id, kind })"
    />
    <chat-feedback
      v-else-if="clarifyTurn"
      key="ai-tutor-feedback-clarify"
      variant="clarify"
      @back="$emit('dismiss-feedback', clarifyTurn.id)"
    />

    <!-- D4 (doc 11): a compact, non-blocking note near the composer. No retry
         control -- the student simply sends again. -->
    <p
      v-if="processingState.phase === 'error'"
      class="ai-tutor-turn-list__error-note"
      role="status"
      data-testid="ai-tutor-stream-error-banner"
    >
      {{ $t('aiTutorSetup.chat.streamErrorBanner') }}
    </p>

    <!-- Doc 27 §28.2 D4: offered only while the student has scrolled away
         and the answer keeps growing below; at visual completion it says so.
         It never appears on its own at the bottom, and nothing scrolls until
         the student asks. Drawn from a list that is empty when there is
         nothing to offer, so that it leaves no node behind. -->
    <div
      v-for="offer in continueOffers"
      :key="offer.key"
      class="ai-tutor-continue"
      data-testid="ai-tutor-continue"
    >
      <button
        type="button"
        class="ai-tutor-continue__button"
        data-testid="ai-tutor-continue-button"
        @click="onContinue"
      >
        <span>{{
          offer.state === 'complete'
            ? $t('aiTutorSetup.chat.answerComplete')
            : $t('aiTutorSetup.chat.continueAnswer')
        }}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
    </div>

    <!-- One announcement when the answer is ready -- the words being
         revealed are never announced one by one. -->
    <p
      class="ai-tutor-turn-list__announcer"
      aria-live="polite"
      data-testid="ai-tutor-answer-announcer"
    >
      {{ announcement }}
    </p>

    <!-- Auto-follow anchor: new turns/streamed text scroll this into view
         (above the fixed composer, via `scroll-margin-bottom`). -->
    <div
      ref="endAnchor"
      class="ai-tutor-turn-list__end"
      aria-hidden="true"
    ></div>

    <!-- POST-UAT-UX / PUX-A (doc 35/01 §1): the cited page, full viewport.
         Its URL is read from the CURRENT turn, so a refetch that re-signs
         it reaches the open viewer; an image that will not load asks the
         host for the existing bounded `media-expired` refetch. -->
    <citation-page-viewer
      v-if="citationView"
      :key="`${citationView.turnId}-${citationView.n}`"
      :n="citationView.n"
      :image-url="citationViewSource.image"
      :source-label="citationViewSource.label"
      @expired="$emit('media-expired', citationView.turnId)"
      @close="closeCitation"
    />
  </div>
</template>

<script>
import TurnBubble from './TurnBubble.vue'
import ChatFeedback from './ChatFeedback.vue'
import ChatSuggestions from './ChatSuggestions.vue'
import ChatAsk from './ChatAsk.vue'
import ChatReadingRecord from './ChatReadingRecord.vue'
import CitationPageViewer from './CitationPageViewer.vue'
import { sourceLine } from './sourceLine'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import { createRevealEngine, REVEAL_MODE } from './reveal/revealEngine'
import { REVEAL_PACING } from './reveal/revealConfig'
import { revealEnvironment } from './reveal/revealEnvironment'
import {
  summarizePresentation,
  recordPresentation,
} from './reveal/presentationMetrics'
import { deriveThinkingPhase } from './thinkingPhase'
import { formatReplyDuration } from './replyDuration'

// How far from the very bottom the student may be and still count as "reading
// the latest message" -- within this the view keeps following new text; beyond
// it (they scrolled up on purpose) it never pulls them back down.
const NEAR_BOTTOM_PX = 80

// Doc 27 (ST-03): presentation records are numbered so a callback of an
// engine that has been replaced can never touch its successor.
let presentationSeq = 0

function countTutorTurns(turns) {
  return (turns || []).filter(turn => turn.role !== 'student').length
}

export default {
  name: 'TurnList',
  components: {
    TurnBubble,
    ChatFeedback,
    ChatSuggestions,
    ChatAsk,
    ChatReadingRecord,
    CitationPageViewer,
    StudentHomeIcon,
  },
  props: {
    /** @type {import('vue').PropOptions<import('@/services/aiTutor/domain').TutorTurn[]>} */
    turns: {
      type: Array,
      default: () => [],
    },
    /** @type {import('vue').PropOptions<import('@/services/aiTutor/domain').TutorProcessingState>} */
    processingState: {
      type: Object,
      default: () => ({ phase: 'idle' }),
    },
    // Forwarded to the student bubbles' avatar (the user's own initial).
    studentInitialSource: {
      type: String,
      default: '',
    },
    // STUDENT CHAT V2 -- the optimistic student message (store
    // `pendingStudentTurn`), drawn after the persisted turns. It replaces the
    // old `holdLive` rule: the message a reply answers is on screen from the
    // moment of submit, so the live reply never has to be held back.
    pendingTurn: {
      type: Object,
      default: null,
    },
    // STUDENT CHAT V2 -- a send or a «نفهمیدم» is waiting on the tutor with
    // no reply text yet: the live bubble shows its waiting state.
    awaitingReply: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2 -- store `feedbackByTurnId`.
    feedbackByTurnId: {
      type: Object,
      default: () => ({}),
    },
    // STUDENT CHAT V2 -- whether the understood/not-understood controls may be
    // offered at all right now (open session, nothing else in flight).
    feedbackEnabled: {
      type: Boolean,
      default: false,
    },
    resendDisabled: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2 image -- store `mediaSourceByTurnId` (caption only).
    mediaSourceByTurnId: {
      type: Object,
      default: () => ({}),
    },
    // WB-M2A (doc 31 §7) -- store `boardShareByTurnId`: the Borna share
    // records of board pictures, by Student turn id. A turn listed here
    // shows the «از تخته» badge on its picture, after a reload too; a photo
    // is never listed and never gets it.
    boardShareByTurnId: {
      type: Object,
      default: () => ({}),
    },
    // FINAL CHAT V2 -- the session's pending question (`pendingAsk`), which
    // only an answer (`resume`) continues.
    pendingAction: {
      type: Object,
      default: null,
    },
    // FINAL CHAT V2 -- suggestion cards and question options are not
    // clickable (something is already in flight, or the chat is not ready).
    actionsDisabled: {
      type: Boolean,
      default: false,
    },
    // Doc 27 (ST-02) -- store `streamStatus`: the event stream's transport,
    // shown only as "reconnecting" and only while a reply is awaited.
    streamStatus: {
      type: String,
      default: 'idle',
    },
  },
  data() {
    return {
      // Doc 27 (ST-03) -- the presentation record of the reply that is live
      // in THIS page lifetime, or null. Presentation state only: it is never
      // written to the store, and the store never reads it. Created when the
      // live bubble appears, so history, a reload or a conversation resumed
      // from Reports can never have one.
      //   key           the SSE `turnId` of the reply's events, once known
      //   tutorBaseline how many tutor turns existed when the wait began
      //   sendAt        the student's Send (presentation clock, ms)
      //   stage         'waiting' | 'handoff' | 'revealing' | 'done'
      //   visible       the visible part of the reply, ready to render
      //   visualDone    everything is visible and no more text will arrive
      //   atomic        shown at once (an ask, a confirmation)
      //   unseen        completed while the student had scrolled away
      //   timedFrom     CHAT-UX-01: the instant the student's wait began, or
      //                 null when this list never saw it begin
      //   durationMs    CHAT-UX-01: `timedFrom` to visual completion
      presentation: null,
      // CHAT-UX-01 (doc 29 §4.1) -- how long the student waited for each
      // reply this list presented, by the persisted reply's id. Presentation
      // state like the record above: never stored, never sent, gone with the
      // list. A reply this list did not present has no entry and shows none.
      replyDurations: {},
      announcement: '',
      // Whether the student is at (or near) the bottom. Tracked from real
      // scroll events -- NOT re-measured after content grows, because a large
      // chunk would otherwise look like "scrolled away" and stop the follow.
      stickToBottom: true,
      // PUX-A: the open citation page -- {turnId, n} -- or null. Page state
      // only; the element that opened it gets the focus back on close.
      citationView: null,
    }
  },
  computed: {
    // PUX-A: the open citation's page image and source line, from the turn
    // as it is NOW (the URL may have been re-signed since it was opened).
    citationViewSource() {
      const view = this.citationView
      const turn = view && this.turns.find(entry => entry.id === view.turnId)
      const source =
        turn && this.sourcesFor(turn).find(entry => entry && entry.n === view.n)
      if (!source) return { image: '', label: '' }
      return {
        image: typeof source.image === 'string' ? source.image : '',
        label: sourceLine(source, (key, values) => this.$t(key, values)),
      }
    },
    showLive() {
      if (
        ['processing', 'streaming', 'reconciling'].includes(
          this.processingState.phase
        )
      ) {
        return true
      }
      // Waiting with nothing from SSE yet. A stream `error` ends the wait
      // visibly (its own note below), never as a spinner that keeps turning.
      return this.awaitingReply && this.processingState.phase !== 'error'
    },
    // STUDENT CHAT V2 image: the optimistic message shows its local preview,
    // dimmed under a spinner until the upload itself has finished.
    pendingMedia() {
      const pending = this.pendingTurn
      if (!pending || !pending.image || !pending.image.previewUrl) return []
      const board = pending.image.source === 'board' || !!pending.boardShare
      return [
        {
          url: pending.image.previewUrl,
          caption: this.captionFor(board ? 'board' : pending.image.source),
          uploading: pending.status === 'uploading',
          board,
          alt: board ? this.$t('aiTutorBoard.share.imageAlt') : '',
        },
      ]
    },
    pendingMeta() {
      const pending = this.pendingTurn
      if (!pending || pending.status === 'failed') return ''
      if (pending.status === 'sending' || pending.status === 'uploading') {
        return this.$t('aiTutorSetup.chat.sendingLabel')
      }
      return this.metaFor({ role: 'student', createdAt: pending.createdAt })
    },
    reteachTurnIds() {
      return Object.values(this.feedbackByTurnId || {})
        .map(entry => entry.reteachTurnId)
        .filter(Boolean)
    },
    // Doc 27 (ST-03) -- the persisted turn of the reply being presented:
    // the tutor turn that answers the record's `turnId`, or, when no event
    // named one (the reply came over HTTP only), the first tutor turn that
    // did not exist when the wait began.
    replyTurn() {
      const record = this.presentation
      if (!record) return null
      const fresh = (this.turns || [])
        .filter(turn => turn.role !== 'student')
        .slice(record.tutorBaseline)
      if (!fresh.length) return null
      const named = record.key
        ? fresh.find(turn => turn.replyToTurnId === record.key)
        : null
      return named || fresh[0]
    },
    // The persisted reply is on screen but not all of it is visible yet.
    presenting() {
      const record = this.presentation
      return !!record && !record.visualDone && !!this.replyTurn
    },
    // THE gate (doc 27 §19, §28.5): a reply is on its way or still being
    // revealed. It delays what waits for the answer -- the feedback prompt,
    // the suggestions, the V405 completion panel -- and changes nothing about
    // what they mean.
    replyInFlight() {
      return this.showLive || this.presenting
    },
    liveVisible() {
      const record = this.presentation
      return record ? record.visible : this.liveText
    },
    // The status is on screen: nothing of the answer is visible yet.
    liveThinking() {
      const record = this.presentation
      if (!record) return !this.liveText
      return record.stage === 'waiting' || record.stage === 'handoff'
    },
    handingOff() {
      return !!this.presentation && this.presentation.stage === 'handoff'
    },
    liveStage() {
      if (this.liveThinking) return 'thinking'
      return this.isStreaming ? 'writing' : ''
    },
    thinkingPhase() {
      return deriveThinkingPhase({
        streamStatus: this.streamStatus,
        processingState: this.processingState,
        pendingTurn: this.pendingTurn,
      })
    },
    thinkingSince() {
      return this.presentation ? this.presentation.sendAt : 0
    },
    continueState() {
      const record = this.presentation
      if (!record || this.stickToBottom) return ''
      if (record.visualDone) return record.unseen ? 'complete' : ''
      return record.stage === 'revealing' ? 'continue' : ''
    },
    // One offer or none; the same element whatever it says.
    continueOffers() {
      return this.continueState
        ? [{ key: 'ai-tutor-continue', state: this.continueState }]
        : []
    },
    // The latest turn, when it is a tutor answer and nothing newer (a send,
    // a live reply) is on screen after it -- the only turn V2 asks about.
    latestTutorTurn() {
      if (this.pendingTurn || this.replyInFlight) return null
      const last = this.turns[this.turns.length - 1]
      return last && last.role !== 'student' ? last : null
    },
    promptTurn() {
      const turn = this.latestTutorTurn
      if (!turn || !this.feedbackEnabled) return null
      // CURRENT CONTRACT AUDIT: a tutor turn carrying `ask` is a question to
      // the student (incl. `clarify`, which never becomes the session's
      // pendingAsk) -- there is nothing to have understood yet.
      if (turn.ask) return null
      const entry = this.feedbackByTurnId[turn.id]
      if (!entry || entry.status === 'failed') return turn
      // A tap being recorded keeps the card, with its controls waiting.
      // AT-API-020-H2: both kinds -- «نفهمیدم» is a Borna learning event that
      // produces no Tutor reply, so there is no waiting row for it either.
      return entry.status === 'pending' ? turn : null
    },
    feedbackBusy() {
      const turn = this.promptTurn
      const entry = turn && this.feedbackByTurnId[turn.id]
      return !!entry && entry.status === 'pending'
    },
    // V405-F1 (doc 26 §12.3): the end of the conversation -- the LAST turn is
    // a tutor answer the student marked «فهمیدم» (recorded), the tutor asks
    // nothing, and nothing is on its way. No timer, no heuristic.
    completionTurn() {
      const turn = this.latestTutorTurn
      if (!turn || turn.ask || this.pendingAction) return null
      return this.isLearned(turn) ? turn : null
    },
    // The tutor's own self-test suggestion on that answer, when it has one.
    practiceSuggestion() {
      const turn = this.completionTurn
      const list = turn && turn.followUpSuggestions
      if (!Array.isArray(list)) return null
      return list.find(s => s && s.kind === 'check') || null
    },
    // «نفهمیدم» recorded and not yet answered: V2's "tell me what is unclear".
    // AT-API-020-H2: the student's clarification is an ordinary turn whose
    // reply the store records as this turn's `reteachTurnId` -- the card
    // closes and that reply carries the retry lead.
    clarifyTurn() {
      const turn = this.latestTutorTurn
      if (!turn) return null
      const entry = this.feedbackByTurnId[turn.id]
      return entry &&
        entry.kind === 'confused' &&
        entry.status === 'recorded' &&
        !entry.reteachTurnId
        ? turn
        : null
    },
    // FINAL CHAT V2 -- the latest tutor answer offers its suggestions when
    // nothing newer is on the way and the tutor is not waiting on a question
    // of its own (upstream never suggests alongside an ask). While one of
    // them is being sent, the cards stay, with that one selected.
    suggestionsTurn() {
      const last = this.turns[this.turns.length - 1]
      if (!last || last.role === 'student' || last.ask) return null
      if (this.pendingAction) return null
      const list = last.followUpSuggestions
      if (!Array.isArray(list) || !list.length) return null
      const pending = this.pendingTurn
      if (pending) {
        const picked =
          pending.suggestion && list.some(s => s.id === pending.suggestion.id)
        return picked ? last : null
      }
      return this.replyInFlight ? null : last
    },
    selectedSuggestionId() {
      const pending = this.pendingTurn
      return (pending && pending.suggestion && pending.suggestion.id) || ''
    },
    // FINAL CHAT V2 -- the session's pending question, under the tutor turn
    // that asked it; kept (with the chosen option selected) while its answer
    // is on its way.
    askTurn() {
      const ask = this.pendingAction
      if (!ask || ask.kind === 'clarify') return null
      const options = Array.isArray(ask.options) ? ask.options : []
      if (!options.length) return null
      const last = this.turns[this.turns.length - 1]
      if (!last || last.role === 'student') return null
      const pending = this.pendingTurn
      if (pending) return pending.answer ? last : null
      return this.replyInFlight ? null : last
    },
    selectedOptionId() {
      const pending = this.pendingTurn
      return (pending && pending.answer && pending.answer.optionId) || ''
    },
    // CHAT-UX-01 / CX-02 (doc 29 §5.2) -- by tutor turn id: the photo reading
    // of every `confirm` question that is no longer the pending one. The
    // text is that turn's own persisted `ask.draft`. The status is read from
    // the turns that follow, and only when they say it:
    //   the student's next message is the «درست است» option itself -> confirmed
    //   it is something else AND the tutor's reply to it was made from the
    //   photo reading (upstream marks only a resumed photo turn so)   -> edited
    //   anything else (no answer yet, its reply not here yet, or the
    //   student wrote a new message instead of answering)             -> none
    readings() {
      const out = {}
      const turns = this.turns || []
      const pendingId = this.askTurn ? this.askTurn.id : null
      // Something is still open: the session asks, a message of the
      // student's is on its way, or a reply is.
      const open =
        !!this.pendingAction || !!this.pendingTurn || this.replyInFlight
      turns.forEach((turn, index) => {
        const ask = turn.role !== 'student' ? turn.ask : null
        if (!ask || ask.kind !== 'confirm' || turn.id === pendingId) return
        const draft = typeof ask.draft === 'string' ? ask.draft.trim() : ''
        if (!draft) return
        const answer = turns.slice(index + 1).find(t => t.role === 'student')
        // Not answered, and something is open: this question is the pending
        // card's, or is about to be (the card is held back while a reply or
        // a message is on its way) -- never a record yet.
        if (!answer && open) return
        out[turn.id] = { draft, status: this.readingStatus(ask, answer, turns) }
      })
      return out
    },
    liveText() {
      return this.processingState.text || ''
    },
    // The reply is still being written: the typing indicator follows the
    // text until all of it is VISIBLE (doc 27), not merely received. With
    // nothing held back that is the old rule: not once `done` has arrived and
    // the complete text is only waiting for the authoritative refetch.
    isStreaming() {
      const record = this.presentation
      if (!record) {
        return (
          this.showLive &&
          !!this.liveText &&
          this.processingState.phase !== 'reconciling'
        )
      }
      return this.showLive && !this.liveThinking && !record.visualDone
    },
    locale() {
      return this.$i18n && this.$i18n.locale === 'fa' ? 'fa-IR' : 'en-US'
    },
  },
  watch: {
    turns(next, previous) {
      this.syncPresentation()
      // A reply whose text was all visible before its persisted turn landed.
      this.noteReplyDuration()
      const added = (next || []).length > (previous || []).length
      const latest = added ? next[next.length - 1] : null
      // The student's own new message always pulls the view to it; anything
      // else (a tutor turn landing, an in-place update) only follows while the
      // student is already at the bottom.
      this.follow(!!latest && latest.role === 'student')
    },
    // Doc 27: text that has arrived is not yet text on screen -- the view
    // follows what becomes VISIBLE (`presentation.visible` below).
    liveText() {
      this.syncPresentation()
    },
    showLive(live, was) {
      if (live && !was) this.beginPresentation()
      this.syncPresentation()
      this.follow(false)
    },
    'processingState.phase'() {
      this.syncPresentation()
    },
    'processingState.turnId'() {
      this.syncPresentation()
    },
    'processingState.ask'() {
      this.syncPresentation()
    },
    'presentation.visible'() {
      this.follow(false)
    },
    'presentation.stage'() {
      this.follow(false)
    },
    stickToBottom(atBottom) {
      if (atBottom && this.presentation) this.presentation.unseen = false
    },
    // STUDENT CHAT V2: the student's own just-sent message always pulls the
    // view to it, exactly like a persisted student turn landing.
    'pendingTurn.localId'(localId) {
      if (!localId) return
      // A new message: a reply that is still being revealed is simply shown.
      // Never the wait this very message has just begun -- its record is
      // already there when this runs, and its reply is to be revealed.
      const record = this.presentation
      if (record && record.stage !== 'waiting') this.showAllNow()
      this.follow(true)
    },
    promptTurn() {
      this.follow(false)
    },
    clarifyTurn() {
      this.follow(false)
    },
    completionTurn() {
      this.follow(false)
    },
    suggestionsTurn() {
      this.follow(false)
    },
    askTurn() {
      this.follow(false)
    },
  },
  created() {
    // Not reactive on purpose: the engine is a live object with a frame
    // pending, not state.
    this.reveal = null
    this.marks = null
    this.stopWatchingMotion = null
    this.followQueued = false
    // The list can be created with a reply already on its way (the first
    // message of a conversation mounts it).
    if (this.showLive) {
      this.beginPresentation()
      this.syncPresentation()
    }
  },
  mounted() {
    // Capture phase on window: `scroll` does not bubble, and the scrolling
    // element may be the document or an inner shell container.
    window.addEventListener('scroll', this.onScroll, true)
    document.addEventListener('visibilitychange', this.onEnvironmentChange)
    document.addEventListener('selectionchange', this.onSelectionChange)
    this.stopWatchingMotion = revealEnvironment.watchReducedMotion(
      this.onEnvironmentChange
    )
    // Opening an existing conversation lands on its latest message.
    this.follow(true)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll, true)
    document.removeEventListener('visibilitychange', this.onEnvironmentChange)
    document.removeEventListener('selectionchange', this.onSelectionChange)
    if (this.stopWatchingMotion) this.stopWatchingMotion()
    this.stopWatchingMotion = null
    this.endPresentation('destroyed')
  },
  methods: {
    // ---------------------------------------------------------------------
    // Doc 27 (ST-03) -- presentation of the live reply
    // ---------------------------------------------------------------------
    // Paced unless the student asked for reduced motion, the preference
    // cannot be read, or nobody is looking (a hidden tab must never build up
    // seconds of text to catch up on).
    presentationMode() {
      if (revealEnvironment.prefersReducedMotion() !== false) {
        return REVEAL_MODE.IMMEDIATE
      }
      return revealEnvironment.isHidden()
        ? REVEAL_MODE.IMMEDIATE
        : REVEAL_MODE.ANIMATED
    },
    beginPresentation() {
      this.endPresentation('superseded')
      presentationSeq += 1
      const id = presentationSeq
      const now = revealEnvironment.now()
      const pending = this.pendingTurn
      const sent = pending ? Date.parse(pending.createdAt) : NaN
      const state = this.processingState || {}
      // CHAT-UX-01: the wait is timed only when this list saw it begin -- a
      // message of the student's on its way, or an awaited reply with none
      // (a «نفهمیدم»). A reply that was already on its way when the list was
      // created with neither (a conversation reopened mid-reply) is not.
      const sending = !!pending && pending.status !== 'failed'
      let timedFrom = null
      if (sending) timedFrom = Number.isNaN(sent) ? now : sent
      else if (this.awaitingReply) timedFrom = now
      const record = {
        id,
        key: state.turnId || null,
        tutorBaseline: countTutorTurns(this.turns),
        sendAt: Number.isNaN(sent) ? now : sent,
        stage: 'waiting',
        visible: '',
        visualDone: false,
        atomic: false,
        unseen: false,
        timedFrom,
        durationMs: null,
      }
      const mine = () => !!this.presentation && this.presentation.id === id
      this.marks = {
        sendAt: record.sendAt,
        waitStartedAt: now,
        firstServerEventAt: null,
        firstServerTextAt: null,
        firstVisibleAt: null,
        receivedDoneAt: null,
        visualDoneAt: null,
      }
      this.announcement = ''
      this.presentation = record
      this.reveal = createRevealEngine({
        now: revealEnvironment.now,
        requestFrame: revealEnvironment.requestFrame,
        cancelFrame: revealEnvironment.cancelFrame,
        pacing: REVEAL_PACING,
        mode: this.presentationMode(),
        onStart: () => {
          if (mine()) this.presentation.stage = 'revealing'
        },
        onUpdate: visible => {
          if (!mine()) return
          if (visible && this.marks.firstVisibleAt === null) {
            this.marks.firstVisibleAt = revealEnvironment.now()
          }
          this.presentation.visible = visible
        },
        onComplete: () => {
          if (mine()) this.completePresentation()
        },
      })
    },
    // Folds the authoritative state -- the store's live text, or the
    // persisted reply once it has landed -- into the presentation. The store
    // is only ever read here.
    syncPresentation() {
      const record = this.presentation
      if (!record || record.visualDone || !this.reveal) return
      const state = this.processingState || {}
      const now = revealEnvironment.now()
      if (!record.key && state.turnId) record.key = state.turnId
      if (
        this.marks.firstServerEventAt === null &&
        ['processing', 'streaming', 'reconciling'].includes(state.phase)
      ) {
        this.marks.firstServerEventAt = now
      }

      const reply = this.replyTurn
      if (reply) {
        // Doc 27 §28.2 D7: a question to the student is never revealed word
        // by word -- it is simply there.
        if (reply.ask) this.makeAtomic()
        this.noteText(now, true)
        this.present(reply.content || '', true, false)
        return
      }
      if (!this.showLive) {
        // The wait ended with no reply: an error, a failed send, a reset.
        this.endPresentation('abandoned')
        return
      }
      if (state.ask) this.makeAtomic()
      const text = this.liveText
      if (!text) return
      const complete = state.phase === 'reconciling'
      this.noteText(now, complete)
      this.present(text, complete, true)
    },
    noteText(now, complete) {
      if (this.marks.firstServerEventAt === null) {
        this.marks.firstServerEventAt = now
      }
      if (this.marks.firstServerTextAt === null) {
        this.marks.firstServerTextAt = now
      }
      if (complete && this.marks.receivedDoneAt === null) {
        this.marks.receivedDoneAt = now
      }
    },
    present(text, complete, fromLiveBubble) {
      const record = this.presentation
      let delayMs = 0
      if (
        record.stage === 'waiting' &&
        fromLiveBubble &&
        text &&
        this.reveal.mode() === REVEAL_MODE.ANIMATED
      ) {
        // the status fades out, then the first word is there
        record.stage = 'handoff'
        delayMs = REVEAL_PACING.handoffMs
      }
      this.reveal.setTarget(text, { complete, delayMs })
    },
    makeAtomic() {
      const record = this.presentation
      if (!record || record.atomic) return
      record.atomic = true
      if (this.reveal) this.reveal.completeNow()
    },
    // Everything received becomes visible; later text is shown as it
    // arrives (a new message from the student, a selection in the bubble).
    showAllNow() {
      const record = this.presentation
      if (!record || record.visualDone || !this.reveal) return
      record.atomic = true
      this.reveal.completeNow()
    },
    completePresentation() {
      const record = this.presentation
      record.visualDone = true
      record.stage = 'done'
      // Completion never moves the viewport; a student who is reading higher
      // up is told, not taken there.
      record.unseen = !this.stickToBottom
      this.marks.visualDoneAt = revealEnvironment.now()
      if (record.timedFrom !== null) {
        record.durationMs = Math.max(
          0,
          this.marks.visualDoneAt - record.timedFrom
        )
      }
      if (!record.atomic) {
        this.announcement = this.$t('aiTutorSetup.chat.answerReady')
      }
      this.publishMetrics('completed')
      if (this.reveal) this.reveal.destroy()
      this.reveal = null
      this.noteReplyDuration()
    },
    // CHAT-UX-01 (doc 29 §4.1) -- the measured wait is kept under the
    // persisted reply it belongs to, once: when the reply is fully visible
    // and its turn is here, whichever comes last.
    noteReplyDuration() {
      const record = this.presentation
      if (!record || !record.visualDone || record.durationMs === null) return
      const reply = this.replyTurn
      if (!reply || this.replyDurations[reply.id] !== undefined) return
      this.$set(this.replyDurations, reply.id, record.durationMs)
      this.follow(false)
    },
    publishMetrics(outcome) {
      if (!this.reveal || !this.marks) return
      const record = this.presentation
      recordPresentation(
        summarizePresentation(this.marks, this.reveal.metrics(), {
          outcome,
          atomic: !!(record && record.atomic),
        })
      )
      this.marks = null
    },
    // Cancels the frame of the engine. 'superseded': a new reply takes the
    // record over; 'abandoned' / 'destroyed': there is nothing to present.
    endPresentation(reason) {
      if (this.reveal) {
        this.publishMetrics(reason)
        this.reveal.destroy()
        this.reveal = null
      }
      if (this.presentation) this.presentation = null
    },
    onEnvironmentChange() {
      const record = this.presentation
      if (!record || record.visualDone || !this.reveal) return
      if (record.atomic) return
      this.reveal.setMode(this.presentationMode())
    },
    onSelectionChange() {
      const record = this.presentation
      if (!record || record.visualDone || record.atomic) return
      const selection =
        typeof window.getSelection === 'function' ? window.getSelection() : null
      if (!selection || selection.isCollapsed || !selection.anchorNode) return
      const bubble = this.$el && this.$el.querySelector('.ai-tutor-turn--live')
      // Text that keeps changing under a selection cannot be selected.
      if (bubble && bubble.contains(selection.anchorNode)) this.showAllNow()
    },
    isPresented(turn) {
      return this.presenting && this.replyTurn.id === turn.id
    },
    contentFor(turn) {
      return this.isPresented(turn) ? this.presentation.visible : turn.content
    },
    onContinue() {
      if (this.presentation) this.presentation.unseen = false
      this.follow(true)
    },
    // A persisted turn's images are the upstream's own signed URLs, re-signed
    // on every refetch (`attachmentUrls` -> adapter `attachments`).
    //
    // WB-M2A: a turn with a Borna share record (or sent from the board in
    // this page) is a board picture: the «از تخته» badge instead of the
    // photo caption. Only Borna's own records decide this -- nothing the
    // Tutor upstream returns can make a photo a board picture.
    mediaFor(turn) {
      if (turn.role !== 'student' || !Array.isArray(turn.attachments)) return []
      const source = (this.mediaSourceByTurnId || {})[turn.id]
      const board =
        !!(this.boardShareByTurnId || {})[turn.id] || source === 'board'
      return turn.attachments
        .filter(item => item.kind === 'image' && item.previewUrl)
        .map(item => ({
          url: item.previewUrl,
          caption: this.captionFor(board ? 'board' : source),
          uploading: false,
          board,
          alt: board ? this.$t('aiTutorBoard.share.imageAlt') : '',
        }))
    },
    // AT-API-020-H3 -- a tutor turn's sources (the `citations` of a 0.2.0
    // reply, as the adapter maps them), listed under the reply once the
    // reveal has shown it -- like the suggestions, never before.
    sourcesFor(turn) {
      if (turn.role === 'student' || !Array.isArray(turn.sources)) return []
      return turn.sources
    },
    // PUX-A: a marker with a page image was activated in this turn's reply.
    openCitation(turn, { n, opener } = {}) {
      if (!turn || !Number.isInteger(n)) return
      this.citationOpener = opener || null
      this.citationView = { turnId: turn.id, n }
    },
    closeCitation() {
      const view = this.citationView
      const opener = this.citationOpener
      this.citationView = null
      this.citationOpener = null
      this.$nextTick(() => {
        // The marker that opened the page -- or, when the reply was redrawn
        // meanwhile, the same marker in the list.
        let target = opener && opener.isConnected ? opener : null
        if (!target && view && this.$el) {
          target = this.$el.querySelector(`[data-cite-open="${view.n}"]`)
        }
        if (target && typeof target.focus === 'function') target.focus()
      })
    },
    captionFor(source) {
      if (source === 'board') return this.$t('aiTutorBoard.share.badge')
      if (source === 'camera')
        return this.$t('aiTutorSetup.chat.image.captionCamera')
      if (source === 'gallery') {
        return this.$t('aiTutorSetup.chat.image.captionGallery')
      }
      return this.$t('aiTutorSetup.chat.image.captionUnknown')
    },
    // The reference's invitation to practise is shown only together with the
    // practice action.
    learnedBody(turn) {
      const body = this.$t('aiTutorSetup.chat.feedback.learnedBody')
      const invites =
        this.practiceSuggestion &&
        this.completionTurn &&
        this.completionTurn.id === turn.id
      return invites
        ? `${body} ${this.$t('aiTutorSetup.chat.completion.practiceInvite')}`
        : body
    },
    isLearned(turn) {
      const entry = this.feedbackByTurnId[turn.id]
      return (
        !!entry && entry.kind === 'understood' && entry.status === 'recorded'
      )
    },
    // CHAT-UX-01 / CX-02 -- see `readings`.
    readingStatus(ask, answer, turns) {
      if (!answer) return ''
      const options = Array.isArray(ask.options) ? ask.options : []
      const confirm = options.find(option => option && option.id === 'confirm')
      const said = typeof answer.content === 'string' ? answer.content : ''
      if (confirm && confirm.label && said.trim() === confirm.label.trim()) {
        return 'confirmed'
      }
      const reply = turns.find(
        turn => turn.role !== 'student' && turn.replyToTurnId === answer.id
      )
      if (!said.trim() || !reply || !reply.fromImageReading) return ''
      // Sent back through «ویرایش می‌کنم» without a change: the same text.
      const draft = typeof ask.draft === 'string' ? ask.draft.trim() : ''
      return said.trim() === draft ? 'confirmed' : 'edited'
    },
    // CHAT-UX-01 (doc 29 §4.1) -- under a tutor reply: how long the student
    // waited for it, from the Send to the whole reply being visible. Only a
    // reply this list presented has one; never a clock time, never while the
    // reply is still being revealed.
    replyDurationFor(turn) {
      if (turn.role === 'student' || this.isPresented(turn)) return ''
      const parts = formatReplyDuration(
        this.replyDurations[turn.id],
        this.locale
      )
      if (!parts) return ''
      return parts.kind === 'seconds'
        ? this.$t('aiTutorSetup.chat.replyDurationSeconds', {
            seconds: parts.value,
          })
        : parts.value
    },
    metaTitleFor(turn) {
      return this.replyDurationFor(turn)
        ? this.$t('aiTutorSetup.chat.replyDurationTitle')
        : ''
    },
    metaFor(turn) {
      if (turn.role !== 'student') return this.replyDurationFor(turn)
      const sent = this.$t('aiTutorSetup.chat.sentLabel')
      const date = new Date(turn.createdAt)
      if (Number.isNaN(date.getTime())) return sent
      const time = date.toLocaleTimeString(this.locale, {
        hour: '2-digit',
        minute: '2-digit',
      })
      return `${time} · ${sent}`
    },
    // The nearest scrollable ancestor, or null when the document scrolls.
    scrollContainer() {
      let el = this.$el && this.$el.parentElement
      while (el && el !== document.body && el !== document.documentElement) {
        const overflowY = window.getComputedStyle(el).overflowY
        if (
          (overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowY === 'overlay') &&
          el.scrollHeight > el.clientHeight
        ) {
          return el
        }
        el = el.parentElement
      }
      return null
    },
    distanceFromBottom() {
      const container = this.scrollContainer()
      if (container) {
        return (
          container.scrollHeight - container.scrollTop - container.clientHeight
        )
      }
      const doc = document.documentElement
      return doc.scrollHeight - (window.scrollY + window.innerHeight)
    },
    onScroll(event) {
      const target = event.target
      const documentScrolled =
        target === window ||
        target === document ||
        target === document.documentElement ||
        target === document.body
      if (
        !documentScrolled &&
        !(target && target.contains && target.contains(this.$el))
      ) {
        return
      }
      this.stickToBottom = this.distanceFromBottom() <= NEAR_BOTTOM_PX
    },
    follow(force) {
      if (force) this.stickToBottom = true
      // One scroll a tick, however many things changed in it (doc 27: a
      // revealed word, the stage and a card can all change together).
      if (this.followQueued) return
      this.followQueued = true
      this.$nextTick(() => {
        this.followQueued = false
        if (!this.stickToBottom) return
        const anchor = this.$refs.endAnchor
        if (!anchor || typeof anchor.scrollIntoView !== 'function') return
        anchor.scrollIntoView({ block: 'end' })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Prototype `.timeline`: column, 18px gap (measured at 1920x1040 and 390x844).
.ai-tutor-turn-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

// STUDENT CHAT V2 -- V2 `.learned-message`: 38px success tile + title/body.
.ai-tutor-turn-list__learned {
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: start;
  gap: 10px;

  > span {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: $borna-teal-50;
    color: $borna-teal-700;
  }

  strong {
    display: block;
    color: $borna-teal-700;
    font-size: 12px;
  }

  p {
    margin: 3px 0 0;
    color: $borna-muted;
  }
}

// ---------------------------------------------------------------------------
// V405-F1 (doc 26 §12) -- the end-of-chat panel. Values measured from the
// RENDERED V405 reference (`BORNA_final405-07-06.html`, sha256
// ea21cc7b...9cfd) at 1440 and 390: `.chat-learned-actions`, its
// `.button--secondary` / `.button--primary` and `.borna-new-conversation`.
// Inset like the feedback cards (avatar 34px + row gap 9px), full width from
// the phone tier down, one column from the reference's 720px tier.
// ---------------------------------------------------------------------------
$completion-inset: 43px;

.ai-tutor-completion {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0 $completion-inset;
  padding: 12px;
  border: 1px solid $borna-teal-100;
  border-radius: 15px;
  background: $borna-teal-50;

  @include borna-compact {
    grid-template-columns: minmax(0, 1fr);
  }

  @include borna-phone {
    margin: 0;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-width: 0;
    margin: 0;
    font-family: inherit;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 3px solid #677fe6;
      outline-offset: 3px;
    }
  }
}

.ai-tutor-completion__secondary,
.ai-tutor-completion__primary {
  gap: 8px;
  height: 40px;
  padding: 0 19px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
}

.ai-tutor-completion__secondary {
  border: 1px solid $borna-border;
  background: $borna-surface;
  color: $borna-text;
}

.ai-tutor-completion__primary {
  border: 1px solid transparent;
  background: $borna-indigo-600;
  color: #fff;
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.22);
}

.ai-tutor-completion__new {
  gap: 7px;
  height: 42px;
  padding: 0 14px;
  border: 1px solid $borna-indigo-300;
  border-radius: 12px;
  background: $borna-surface;
  color: $borna-indigo-900;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.75;

  @include borna-compact {
    height: 44px;
    font-size: 12px;
  }
}

// ---------------------------------------------------------------------------
// Doc 27 §28.2 D4 -- «ادامه پاسخ». Measured from the rendered design
// candidate (`.st-pill`): a 34px pill, 92px above the viewport floor on
// desktop and 145px in the mobile shell, i.e. 12px above the fixed composer.
// Sticky inside the list, so it sits on the chat column's own axis in both
// directions without repeating the composer's geometry. It takes no room:
// the negative margin gives the list's 18px gap back.
// ---------------------------------------------------------------------------
.ai-tutor-continue {
  position: sticky;
  bottom: 92px;
  z-index: 17;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 0;
  margin-top: -18px;
  pointer-events: none;

  @include borna-mobile {
    bottom: calc(
      #{$borna-bottom-nav-clearance + 72px} + env(safe-area-inset-bottom)
    );
  }
}

.ai-tutor-continue__button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  box-sizing: border-box;
  height: 34px;
  margin: 0;
  padding: 0 13px;
  border: 1px solid $borna-indigo-100;
  border-radius: $borna-radius-pill;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 5px 16px rgba(49, 75, 214, 0.14);
  color: $borna-indigo-900;
  font-family: inherit;
  font-size: 10px;
  font-weight: $borna-fw-extrabold;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
  pointer-events: auto;
  animation: ai-tutor-continue-in 0.2s ease both;

  &:focus-visible {
    outline: 3px solid #677fe6;
    outline-offset: 3px;
  }
}

@keyframes ai-tutor-continue-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-tutor-continue__button {
    animation: none;
  }
}

// Heard, not seen; out of the flow, so it takes no gap of the list.
.ai-tutor-turn-list__announcer {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.ai-tutor-turn-list__error-note {
  align-self: flex-start;
  max-width: 100%;
  margin: 0;
  padding: 8px 12px;
  border: 1px solid #f3b9b3;
  border-radius: 12px;
  background: #fdecea;
  color: #b3261e;
  font-size: 11px;
}

// Room so an anchored scroll never lands the last turn under the fixed
// composer (desktop: 60px composer + 20px offset; mobile: + bottom nav).
// WP-04V-D (R8): the mobile value is derived from the bottom nav's own token
// rather than asserted as a third independent number.
.ai-tutor-turn-list__end {
  height: 0;
  scroll-margin-bottom: 110px;

  @include borna-mobile {
    scroll-margin-bottom: $borna-bottom-nav-clearance + 110px;
  }
}
</style>
